import { useEffect, useSyncExternalStore } from 'react';
import { type ThemePreference, useLocalPrefs } from './local-prefs';

export type ResolvedTheme = 'light' | 'dark';

const DARK_QUERY = '(prefers-color-scheme: dark)';
const THEME_COLOR: Record<ResolvedTheme, string> = { light: '#fafafa', dark: '#09090b' };

function subscribeToSystemTheme(onChange: () => void): () => void {
  const media = window.matchMedia(DARK_QUERY);
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}

function systemPrefersDark(): boolean {
  return window.matchMedia(DARK_QUERY).matches;
}

export function resolveTheme(preference: ThemePreference, prefersDark: boolean): ResolvedTheme {
  if (preference === 'system') return prefersDark ? 'dark' : 'light';
  return preference;
}

/** The theme actually in effect (preference + OS setting), live-updating. */
export function useResolvedTheme(): ResolvedTheme {
  const preference = useLocalPrefs((s) => s.theme);
  const prefersDark = useSyncExternalStore(subscribeToSystemTheme, systemPrefersDark, () => false);
  return resolveTheme(preference, prefersDark);
}

/** Keeps <html class="dark"> and the browser theme colour in sync with the resolved theme. */
export function ThemeController(): null {
  const theme = useResolvedTheme();
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', theme === 'dark');
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
  }, [theme]);
  return null;
}
